import React, { useEffect, useState } from "react";
import { database, ref, push, onValue, update, remove } from "./firebase";
import "./BillingSystem.css";
import Navigation from "./Navigation";

/* 🔹 FIXED ORDER */
const PRODUCT_ORDER = [
  "Pink 2L",
  "Pink 1L",
  "Pink 500ml",
  "Pink 250ml",
  "Green 1L",
  "Green 500ml",
  "Pouch",
];

export default function BottlersSP() {
  const [products, setProducts] = useState([]);
  const [entries, setEntries] = useState([]);

  const [name, setName] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [cash, setCash] = useState("");
  const [online, setOnline] = useState("");
  const [editId, setEditId] = useState(null);

  /* 🔹 PAYMENT MODAL STATE */
  const [payEntry, setPayEntry] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState(new Date().toISOString().split("T")[0]);
  const [payMode, setPayMode] = useState("cash");

  /* 🔹 LOAD PRODUCTS (SAFE) */
  useEffect(() => {
    onValue(ref(database, "products"), (snap) => {
      if (!snap.exists()) {
        setProducts([]);
        return;
      }

      const list = Object.values(snap.val()).map((p) => ({
        name: p?.name || "",
        price: Number(p?.rate) || 0,
        quantity: 0,
      }));

      setProducts(list);
    });
  }, []);

  /* 🔹 LOAD HISTORY (SAFE) */
  useEffect(() => {
    onValue(ref(database, "bottlersSPHistory"), (snap) => {
      if (!snap.exists()) {
        setEntries([]);
        return;
      }

      const list = Object.entries(snap.val()).map(([id, v]) => ({
        id,
        ...v,
        products: Array.isArray(v.products) ? v.products : [], // ✅ SAFE
      }));

      list.sort((a, b) => new Date(b.date) - new Date(a.date));
      setEntries(list);
    });
  }, []);

  const calcAmount = (p) =>
    Number(p?.quantity || 0) * Number(p?.price || 0);

  const totalAmount = products.reduce((s, p) => s + calcAmount(p), 0);

  const balanceAmount =
    totalAmount - (Number(cash || 0) + Number(online || 0));

  /* 🔹 SAVE ENTRY */
  const saveEntry = () => {
    if (!name.trim()) return alert("Enter name");

    const record = {
      name,
      date,
      products: products.filter((p) => Number(p.quantity) > 0),
      totalAmount,
      cashAmount: Number(cash || 0),
      onlineAmount: Number(online || 0),
      balanceAmount,
      isPaid: balanceAmount <= 0,
    };

    editId
      ? update(ref(database, `bottlersSPHistory/${editId}`), record)
      : push(ref(database, "bottlersSPHistory"), record);

    setName("");
    setCash("");
    setOnline("");
    setEditId(null);
    setProducts(products.map((p) => ({ ...p, quantity: 0 })));
  };

  /* 🔹 EDIT (CRASH-PROOF) */
  const editEntry = (e) => {
    setEditId(e.id);
    setName(e.name || "");
    setDate(e.date);
    setCash(e.cashAmount || "");
    setOnline(e.onlineAmount || "");

    setProducts(
      products.map((p) => {
        const match = (e.products || []).find(
          (x) => x?.name === p.name
        );
        return match ? { ...p, ...match } : { ...p, quantity: 0 };
      })
    );
  };

  /* 🔹 DELETE */
  const deleteEntry = (id) => {
    if (window.confirm("Delete entry?")) {
      remove(ref(database, `bottlersSPHistory/${id}`));
    }
  };

  /* 🔹 SAVE PAYMENT */
  const savePayment = () => {
    const amt = Number(payAmount);
    if (amt <= 0 || amt > payEntry.balanceAmount)
      return alert("Invalid amount");

    const updates = {
      balanceAmount: payEntry.balanceAmount - amt,
      cashAmount:
        payMode === "cash"
          ? Number(payEntry.cashAmount || 0) + amt
          : payEntry.cashAmount || 0,
      onlineAmount:
        payMode === "online"
          ? Number(payEntry.onlineAmount || 0) + amt
          : payEntry.onlineAmount || 0,
    };

    if (updates.balanceAmount === 0) updates.isPaid = true;

    update(ref(database, `bottlersSPHistory/${payEntry.id}`), updates);
    setPayEntry(null);
  };

  /* 🔹 GROUP BY DATE (SAFE) */
  const grouped = entries.reduce((a, e) => {
    if (!e.date) return a;
    (a[e.date] = a[e.date] || []).push(e);
    return a;
  }, {});

  return (
    <div className="history-container">
      <Navigation role="special manager" />

      <h2 className="page-title">💧 Bottlers Entry</h2>

      {/* ENTRY FORM */}
      <table className="styled-table">
        <thead>
          <tr>
            <th>Product</th>
            <th>Qty</th>
            <th>Price</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p, i) => (
            <tr key={i}>
              <td>{p.name}</td>
              <td>
                <input
                  type="number"
                  className="input-small"
                  value={p.quantity || ""}
                  onChange={(e) =>
                    setProducts(
                      products.map((x, j) =>
                        j === i ? { ...x, quantity: e.target.value } : x
                      )
                    )
                  }
                />
              </td>
              <td>
                <input
                  type="number"
                  className="input-small"
                  value={p.price}
                  onChange={(e) =>
                    setProducts(
                      products.map((x, j) =>
                        j === i ? { ...x, price: e.target.value } : x
                      )
                    )
                  }
                />
              </td>
              <td>₹{calcAmount(p)}</td>
            </tr>
          ))}
          <tr>
            <td colSpan="3"><b>Total</b></td>
            <td><b>₹{totalAmount}</b></td>
          </tr>
        </tbody>
      </table>

      <div className="entry-controls">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" />
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <input placeholder="Cash ₹" value={cash} onChange={(e) => setCash(e.target.value)} />
        <input placeholder="Online ₹" value={online} onChange={(e) => setOnline(e.target.value)} />
        <button className="save-btn" onClick={saveEntry}>💾 Save Entry</button>
      </div>

      {/* HISTORY */}
      {Object.keys(grouped).map((d) => (
        <div key={d}>
          <h4>{d}</h4>
          <table className="styled-table">
            <thead>
              <tr>
                <th>Name</th>
                {PRODUCT_ORDER.map((p) => <th key={p}>{p}</th>)}
                <th>Total</th>
                <th>Cash</th>
                <th>Online</th>
                <th>Balance</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {grouped[d].map((e) => (
                <tr key={e.id}>
                  <td><b>{e.name}</b></td>

                  {PRODUCT_ORDER.map((p) => {
                    const pr = (e.products || []).find(
                      (x) => x?.name === p
                    );
                    return (
                      <td key={p}>
                        {pr ? `${pr.quantity} @ ₹${pr.price}` : "—"}
                      </td>
                    );
                  })}

                  <td>₹{e.totalAmount}</td>
                  <td>₹{e.cashAmount || 0}</td>
                  <td>₹{e.onlineAmount || 0}</td>
                  <td style={{ color: e.isPaid ? "green" : "red" }}>
                    ₹{e.balanceAmount}
                  </td>
                  <td>{e.isPaid ? "✅ Paid" : "⏳ Pending"}</td>
                  <td>
                    <button onClick={() => editEntry(e)}>✏️</button>
                    <button onClick={() => deleteEntry(e.id)}>🗑️</button>
                    {!e.isPaid && (
                      <button onClick={() => {
                        setPayEntry(e);
                        setPayAmount(e.balanceAmount);
                      }}>
                        🧾 Amount Paid
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {/* PAYMENT MODAL */}
      {payEntry && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h3>🧾 Record Payment</h3>
            <input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
            <input value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
            <select value={payMode} onChange={(e) => setPayMode(e.target.value)}>
              <option value="cash">Cash</option>
              <option value="online">Online</option>
            </select>
            <button onClick={savePayment}>Save</button>
            <button onClick={() => setPayEntry(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
